/**
 * Tiny SSR-safe i18n helper. Loads namespace JSON files synchronously
 * via Vite's `import.meta.glob` so SSR and client share the same source.
 *
 * Lookups are dotted paths (`hero.title`) with optional `{var}`
 * interpolation. Unknown keys return the key itself for visible debug.
 *
 * Supported locales come from `./i18n/locales.ts` — the single source of truth.
 */

import {
  DEFAULT_LOCALE,
  isLocaleCode,
  type LocaleCode,
} from "./i18n/locales.js";

type Bundle = Record<string, unknown>;

const modules = import.meta.glob<{ default: Bundle }>(
  "../../public/locales/*/*.json",
  { eager: true },
);

const cache = new Map<string, Bundle>();

function getBundle(locale: LocaleCode, namespace: string): Bundle {
  const key = `${locale}:${namespace}`;
  if (cache.has(key)) return cache.get(key) as Bundle;
  const path = `../../public/locales/${locale}/${namespace}.json`;
  const mod = modules[path];
  const bundle = (mod?.default ?? {}) as Bundle;
  cache.set(key, bundle);
  return bundle;
}

function lookup(bundle: Bundle, dotted: string): unknown {
  const parts = dotted.split(".");
  let cur: unknown = bundle;
  for (const p of parts) {
    if (typeof cur !== "object" || cur === null) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k];
    return v == null ? `{${k}}` : String(v);
  });
}

export type TranslateFn = (
  key: string,
  vars?: Record<string, string | number>,
) => string;

export function createT(locale: string, namespace = "landing"): TranslateFn {
  const safeLocale: LocaleCode = isLocaleCode(locale) ? locale : DEFAULT_LOCALE;

  return (key: string, vars?: Record<string, string | number>): string => {
    // Support `namespace:key.path` shape
    let ns = namespace;
    let lookupKey = key;
    const colonIdx = key.indexOf(":");
    if (colonIdx > -1) {
      ns = key.slice(0, colonIdx);
      lookupKey = key.slice(colonIdx + 1);
    }
    const value =
      lookup(getBundle(safeLocale, ns), lookupKey) ??
      lookup(getBundle(DEFAULT_LOCALE, ns), lookupKey);
    if (typeof value === "string") return interpolate(value, vars);
    return key;
  };
}

/**
 * Read the `_meta` block (if any) from a namespace bundle.
 * Used by the MachineTranslationBanner to decide whether to show the disclaimer.
 */
export function getNamespaceMeta(
  locale: string,
  namespace: string,
): { machineTranslated?: boolean; sourceHash?: string; translatedAt?: string; model?: string } {
  const safeLocale: LocaleCode = isLocaleCode(locale) ? locale : DEFAULT_LOCALE;
  const meta = (getBundle(safeLocale, namespace) as { _meta?: unknown })._meta;
  return (meta && typeof meta === "object" ? meta : {}) as ReturnType<typeof getNamespaceMeta>;
}

export function formatNumber(value: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch {
    return String(value);
  }
}

export function formatDate(
  value: string | Date,
  locale: string,
  opts: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
): string {
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    return new Intl.DateTimeFormat(locale, opts).format(d);
  } catch {
    return String(value);
  }
}

export function formatPercent(value: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "percent",
      maximumFractionDigits: 2,
      minimumFractionDigits: 1,
    }).format(value / 100);
  } catch {
    return `${value}%`;
  }
}
